import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../lib/api';
import { Id } from '../convex/_generated/dataModel';

export default function PlayerDashboard() {
  const navigate = useNavigate();
  const [playerId, setPlayerId] = useState<Id<"players"> | null>(null);
  
  // Get player ID from localStorage
  useEffect(() => {
    const storedPlayerId = localStorage.getItem('playerId');
    if (!storedPlayerId) {
      navigate('/login');
    } else {
      setPlayerId(storedPlayerId as Id<"players">);
    }
  }, [navigate]);

  // Fetch player data
  const currentUser = useQuery(api.auth.getCurrentUser, 
    playerId ? { playerId } : 'skip'
  );
  
  const wonTournaments = useQuery(api.playerDashboard.getWonTournaments,
    playerId ? { playerId } : 'skip'
  );
  
  const futureTournaments = useQuery(api.playerDashboard.getFutureTournaments,
    playerId ? { playerId } : 'skip'
  );
  
  const pastTournaments = useQuery(api.playerDashboard.getPastTournaments,
    playerId ? { playerId } : 'skip'
  );

  const removeFromTournament = useMutation(api.players.removeFromTournament);

  const handleUnregister = async (tournamentId: Id<"tournaments">) => {
    if (!playerId) return;
    
    if (window.confirm('¿Estás seguro de que quieres cancelar tu registro?')) {
      try {
        await removeFromTournament({ tournamentId, playerId });
      } catch (error) {
        console.error('Error unregistering:', error);
      }
    }
  };

  const handleShare = (tournament: { name: string; club: string }) => {
    const text = `🏆 ¡Gané el torneo ${tournament.name} en ${tournament.club}! 🎾\n\nOrganiza tus torneos en TorneoPadel.co`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Victoria en Torneo de Pádel',
        text,
        url: 'https://torneopadel.co',
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(text);
      alert('Mensaje copiado al portapapeles');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  if (!currentUser || !playerId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="/torneopadel.png" 
              alt="TorneoPadel" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Hola, {currentUser.name}</h1>
              <p className="text-sm text-gray-600">Panel de Jugador</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Link
              to="/profile"
              className="text-gray-600 hover:text-gray-900"
            >
              {currentUser.profilePicture ? (
                <img 
                  src={currentUser.profilePicture} 
                  alt="Profile" 
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {currentUser.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Won Tournaments */}
        {wonTournaments && wonTournaments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              🏆 Mis Victorias
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wonTournaments.map((tournament: any) => (
                <div key={tournament._id} className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-300">
                  <h3 className="font-semibold text-lg">{tournament.name}</h3>
                  <p className="text-sm text-gray-600">{tournament.club}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(tournament.date).toLocaleDateString('es-MX')} - {tournament.time}
                  </p>
                  <button
                    onClick={() => handleShare(tournament)}
                    className="mt-3 w-full bg-yellow-500 text-white py-2 rounded-lg hover:bg-yellow-600 transition-colors font-medium"
                  >
                    Compartir Victoria 🎉
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Future Tournaments */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Próximos Torneos</h2>
          {futureTournaments && futureTournaments.length > 0 ? (
            <div className="space-y-4">
              {futureTournaments.filter((t: any): t is NonNullable<typeof t> => t !== null).map((tournament: any) => (
                <div key={tournament._id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold">{tournament.name}</h3>
                      <p className="text-gray-600">{tournament.club}</p>
                      <p className="text-gray-600">
                        {new Date(tournament.date).toLocaleDateString('es-MX')} - {tournament.time}
                      </p>
                      <p className="text-sm text-gray-500 capitalize">Formato: {tournament.format}</p>
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                        tournament.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {tournament.status === 'confirmed' ? 'Confirmado' : 'Lista de Espera'}
                      </span>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <Link
                        to={`/tournament/${tournament.inviteCode}`}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Ver Detalles
                      </Link>
                      <button
                        onClick={() => handleUnregister(tournament._id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        Cancelar Registro
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No tienes torneos próximos</p>
              <Link
                to="/join"
                className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Buscar Torneos
              </Link>
            </div>
          )}
        </div>

        {/* Past Tournaments */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Historial de Torneos</h2>
          {pastTournaments && pastTournaments.length > 0 ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Torneo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Club
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resultado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pastTournaments.filter((t: any): t is NonNullable<typeof t> => t !== null).map((tournament: any) => (
                    <tr key={tournament._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{tournament.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{tournament.club}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(tournament.date).toLocaleDateString('es-MX')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {tournament.isWinner ? (
                          <span className="text-yellow-600 font-semibold">🏆 Ganador</span>
                        ) : (
                          <span className="text-gray-500">Participante</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">No has participado en torneos anteriores</p>
            </div>
          )}
        </div>

        {/* Waitlist Banner */}
        {currentUser.role === 'player' && !currentUser.waitList && (
          <div className="mt-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
            <h3 className="text-xl font-bold mb-2">¿Quieres organizar torneos?</h3>
            <p className="mb-4">Únete a la lista de espera para convertirte en organizador</p>
            <Link
              to="/waitlist"
              className="inline-block px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
            >
              Aplicar Ahora
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}